import React from 'react';
import './PrintableWorkOrderReport.css'; // Assuming a similar CSS file structure
import { format } from 'date-fns';

// Interfaces based on data structure from report.controller.js getWorkOrderReport
interface WorkOrderReportItem {
  id: number;
  workOrderNumber: string;
  type: string;
  clientName: string;
  orderNumber: string;
  structureCode: string;
  structureLocation: string;
  assignedToName: string;
  status: string;
  scheduledDate: string;
  completedDate?: string | null;
  completionTime?: number | null;
}

interface WorkOrderReportData {
  workOrders: WorkOrderReportItem[];
  startDate: string; // Need to pass these from the print page component
  endDate: string;   // Need to pass these from the print page component
  statusFilter?: string; // Optional filter info
  typeFilter?: string;   // Optional filter info
}

interface PrintableWorkOrderReportProps {
  reportData: WorkOrderReportData;
}

// Helper to format dates
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'dd.MM.yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper for status text
const getStatusText = (status: string): string => {
  const statuses: { [key: string]: string } = {
    pending: 'Ожидает',
    inProgress: 'В процессе',
    completed: 'Выполнен',
    cancelled: 'Отменен',
    delayed: 'Отложен'
  };
  return statuses[status] || status;
};

// Helper for type text
const getTypeText = (type: string): string => {
  const types: { [key: string]: string } = {
    installation: 'Установка',
    removal: 'Демонтаж',
    maintenance: 'Обслуживание',
    videoReport: 'Видеоотчет'
  };
  return types[type] || type;
};

const PrintableWorkOrderReport: React.FC<PrintableWorkOrderReportProps> = ({ reportData }) => {
  if (!reportData || !reportData.workOrders) {
    return <div className="printable-container">Ошибка: Данные для отчета по нарядам не найдены.</div>;
  }

  const {
    workOrders,
    startDate,
    endDate,
    statusFilter,
    typeFilter,
  } = reportData;

  return (
    <div className="printable-container work-order-report-container">
      <header className="printable-header">
        <h1>Отчет по нарядам</h1>
        <p>Период: {formatDate(startDate)} - {formatDate(endDate)}</p>
        {statusFilter && statusFilter !== 'all' && <p>Статус: {getStatusText(statusFilter)}</p>}
        {typeFilter && typeFilter !== 'all' && <p>Тип: {getTypeText(typeFilter)}</p>}
      </header>

      <section className="details-table">
        <h2>Детализация по нарядам</h2>
        <table>
          <thead>
            <tr>
              <th>Номер</th>
              <th>Тип</th>
              <th>Клиент</th>
              <th>Конструкция</th>
              <th>Исполнитель</th>
              <th>План. дата</th>
              <th>Статус</th>
              <th>Дата вып.</th>
            </tr>
          </thead>
          <tbody>
            {workOrders.map((wo) => (
              <tr key={wo.id}>
                <td>{wo.workOrderNumber}</td>
                <td>{getTypeText(wo.type)}</td>
                <td>{wo.clientName}</td>
                <td>{wo.structureCode} ({wo.structureLocation})</td>
                <td>{wo.assignedToName}</td>
                <td>{formatDate(wo.scheduledDate)}</td>
                <td>{getStatusText(wo.status)}</td>
                <td>{formatDate(wo.completedDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <footer className="printable-footer">
        Отчет сформирован: {formatDate(new Date())}
      </footer>
    </div>
  );
};

export default PrintableWorkOrderReport;
